/**
 * ScenarioMatrixTab - „Wann gewinnt der 3-Topf?"
 *
 * Rechnet die volle Szenario-Matrix (Inflations- × Rendite-Optionen ×
 * Entnahmeraten-Sweep) und vergleicht 3-Topf vs. 1-Topf je Zelle auf TAIL-Metriken
 * (P5/P10-Endkapital mit Verarmt-⇒-0-Regel, Verarmungsrisiko) statt Mittelwerten.
 *
 * Die eigentliche Wertfrage der App — schützt der 3-Topf im Tail? — wird hier
 * direkt und ehrlich beantwortet.
 *
 * Muster: CombinedOptimizationTab.tsx
 */

import React, { useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  ColumnLayout,
  Container,
  FormField,
  Header,
  Input,
  Select,
  SpaceBetween,
  Table,
} from '@cloudscape-design/components';
import {
  ScenarioCellResult,
  ScenarioMatrixConfig,
  ScenarioVerdict,
  ThreeTopfSimulationOptions as SimulationOptions,
} from '@/types/simulation';
import { CombinedMonteCarloEngine } from '@/services/CombinedMonteCarloEngine';
import { SteuerParameter } from '@/types';
import {
  INFLATION_OPTIONS,
  INVESTMENT_RETURN_OPTIONS,
  getInflationShortLabel,
  getInvestmentReturnShortLabel,
} from '@/utils/optionLabels';

interface ScenarioMatrixTabProps {
  steuerParameter: SteuerParameter | null;
  monteCarloEngine: CombinedMonteCarloEngine | undefined;
  iterations: number;
  simulationOptions?: SimulationOptions;
}

// Geschätzter Durchsatz (Iterationen pro Sekunde, beide Strategien) für die
// Laufzeitschätzung. Grober Erfahrungswert, bewusst benannt.
const ESTIMATED_ITERATIONS_PER_SECOND = 285;

// Anzahl der Basiszellen je Entnahmerate: 4 Inflations- × 6 Rendite-Optionen.
const BASE_CELLS_PER_RATE = INFLATION_OPTIONS.length * INVESTMENT_RETURN_OPTIONS.length;

// EUR-Formatierung (deutsch, ohne Nachkommastellen).
const formatEur = (value: number): string =>
  value.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });

// Relativer P10-Unterschied (3-Topf vs. 1-Topf) in Prozent, aus Sicht des 3-Topfs.
const relativeP10Delta = (cell: ScenarioCellResult): number => {
  const scale = Math.max(cell.threeTopf.p10, cell.oneTopf.p10);
  if (scale <= 0) {
    return 0;
  }
  return ((cell.threeTopf.p10 - cell.oneTopf.p10) / scale) * 100;
};

// Badge-Farbe + Text je Urteil.
const verdictBadge = (verdict: ScenarioVerdict): React.ReactNode => {
  switch (verdict) {
    case '3topf':
      return <Badge color="green">3-Topf</Badge>;
    case '1topf':
      return <Badge color="blue">1-Topf</Badge>;
    case 'gemischt':
      return <Badge color="grey">Gemischt</Badge>;
    default:
      return <Badge color="grey">Unentschieden</Badge>;
  }
};

export const ScenarioMatrixTab: React.FC<ScenarioMatrixTabProps> = ({
  steuerParameter,
  monteCarloEngine,
  iterations,
  simulationOptions,
}) => {
  const [config, setConfig] = useState<ScenarioMatrixConfig>({
    minRate: 2.0,
    maxRate: 6.0,
    stepSize: 1.0,
    iterationsPerCell: Math.min(iterations, 400),
  });
  const [results, setResults] = useState<ScenarioCellResult[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [currentCellLabel, setCurrentCellLabel] = useState<string | undefined>();
  const [currentCellIndex, setCurrentCellIndex] = useState<number | undefined>();
  const [totalCells, setTotalCells] = useState<number | undefined>();

  // Filter-State der Matrix-Tabelle.
  const [filterReturn, setFilterReturn] = useState<string>('all');
  const [filterInflation, setFilterInflation] = useState<string>('all');
  const [filterVerdict, setFilterVerdict] = useState<string>('all');

  // Zellzahl + Laufzeitschätzung.
  const rateCount = useMemo(() => {
    if (config.stepSize <= 0 || config.maxRate < config.minRate) {
      return 0;
    }
    return Math.floor((config.maxRate - config.minRate) / config.stepSize + 1e-9) + 1;
  }, [config.minRate, config.maxRate, config.stepSize]);

  const cellCount = BASE_CELLS_PER_RATE * rateCount;
  const totalSims = cellCount * config.iterationsPerCell;
  const estimatedSeconds = Math.round(totalSims / ESTIMATED_ITERATIONS_PER_SECOND);
  const estimatedMinutes = Math.max(1, Math.round(estimatedSeconds / 60));

  const isConfigValid = config.minRate < config.maxRate && config.stepSize > 0;

  const handleRun = async () => {
    if (!monteCarloEngine || !steuerParameter || !isConfigValid) {
      return;
    }
    setIsRunning(true);
    setProgress(0);
    setResults([]);
    try {
      const matrixResults = await monteCarloEngine.runScenarioMatrixComparison(
        config,
        (prog, cellLabel, cellIndex, total) => {
          // Rerender-Drossel: setProgress nur bei ganzzahliger Änderung.
          const rounded = Math.round(prog);
          setProgress(prev => (prev !== rounded ? rounded : prev));
          setCurrentCellLabel(cellLabel);
          setCurrentCellIndex(cellIndex);
          setTotalCells(total);
        }
      );
      setResults(matrixResults);
    } catch (error) {
      console.error('Szenario-Matrix fehlgeschlagen:', error);
    } finally {
      setIsRunning(false);
      setProgress(0);
      setCurrentCellLabel(undefined);
      setCurrentCellIndex(undefined);
      setTotalCells(undefined);
    }
  };

  // KPI-Zähler.
  const kpi = useMemo(() => {
    const wins3 = results.filter(r => r.verdict === '3topf').length;
    const wins1 = results.filter(r => r.verdict === '1topf').length;
    const mixed = results.filter(r => r.verdict === 'gemischt').length;
    const undecided = results.filter(r => r.verdict === 'unentschieden').length;
    const p10Three = results.filter(r => r.p10Axis === 'threeTopf').length;
    const p10One = results.filter(r => r.p10Axis === 'oneTopf').length;
    const riskThree = results.filter(r => r.riskAxis === 'threeTopf').length;
    const riskOne = results.filter(r => r.riskAxis === 'oneTopf').length;
    return { wins3, wins1, mixed, undecided, p10Three, p10One, riskThree, riskOne };
  }, [results]);

  // Datengetriebener Satz: Rendite-Modell mit höchstem 3-Topf-Anteil.
  const bestReturnModel = useMemo(() => {
    if (results.length === 0) {
      return null;
    }
    let best: { option: string; share: number; wins: number; total: number } | null = null;
    for (const option of INVESTMENT_RETURN_OPTIONS) {
      const cells = results.filter(r => r.investmentReturnOption === option);
      if (cells.length === 0) {
        continue;
      }
      const wins = cells.filter(r => r.verdict === '3topf').length;
      const share = wins / cells.length;
      if (!best || share > best.share) {
        best = { option, share, wins, total: cells.length };
      }
    }
    return best;
  }, [results]);

  // Raten-Cluster mit 3-Topf-Siegen (min/max Rate, in der der 3-Topf gewinnt).
  const winningRateCluster = useMemo(() => {
    const rates = results.filter(r => r.verdict === '3topf').map(r => r.withdrawalRate);
    if (rates.length === 0) {
      return null;
    }
    return { min: Math.min(...rates), max: Math.max(...rates) };
  }, [results]);

  // Ehrliches Fazit.
  const verdictSummary = useMemo(() => {
    if (results.length === 0) {
      return null;
    }
    const decided = kpi.wins3 + kpi.wins1;
    let type: 'success' | 'warning' | 'info';
    let header: string;
    let body: string;

    if (decided > 0 && kpi.wins3 / decided >= 0.6) {
      type = 'success';
      header = 'Der 3-Topf schützt im Tail';
      body =
        `In der Mehrheit der entschiedenen Szenarien (${kpi.wins3} von ${decided}) ` +
        `liefert der 3-Topf den besseren Tail-Schutz — höheres P10-Endkapital und/oder ` +
        `niedrigeres Verarmungsrisiko.`;
    } else if (decided > 0 && kpi.wins1 / decided >= 0.6) {
      type = 'warning';
      header = 'Der 1-Topf ist im Tail überlegen';
      body =
        `Der 1-Topf ist im Tail in den meisten Szenarien überlegen ` +
        `(${kpi.wins1} von ${decided} entschiedenen Zellen). Der Cash-Drag des 3-Topfs ` +
        `frisst den Krisenschutz auch im schlechten Ausgang.`;
    } else {
      type = 'info';
      header = 'Es hängt vom Szenario ab';
      body =
        `Kein klarer Tail-Gewinner: 3-Topf gewinnt ${kpi.wins3}, 1-Topf ${kpi.wins1}, ` +
        `gemischt ${kpi.mixed}, unentschieden ${kpi.undecided} von ${results.length} Zellen. ` +
        `Welche Strategie im Tail besser schützt, hängt vom konkreten Renditemodell und ` +
        `der Entnahmerate ab.`;
    }

    // Datengetriebener Zusatzsatz.
    let extra = '';
    if (bestReturnModel && bestReturnModel.wins > 0) {
      extra =
        ` Am stärksten schneidet der 3-Topf beim Renditemodell ` +
        `„${getInvestmentReturnShortLabel(bestReturnModel.option)}" ab ` +
        `(${bestReturnModel.wins} von ${bestReturnModel.total} Zellen)`;
      if (winningRateCluster) {
        if (winningRateCluster.min === winningRateCluster.max) {
          extra += `, konzentriert bei ${winningRateCluster.min}% Entnahmerate`;
        } else {
          extra +=
            `, vor allem bei Entnahmeraten zwischen ${winningRateCluster.min}% und ` +
            `${winningRateCluster.max}%`;
        }
      }
      extra += '.';
    }

    return { type, header, body: body + extra };
  }, [results, kpi, bestReturnModel, winningRateCluster]);

  // Gefilterte Tabellen-Items.
  const filteredResults = useMemo(() => {
    return results.filter(r => {
      if (filterReturn !== 'all' && r.investmentReturnOption !== filterReturn) {
        return false;
      }
      if (filterInflation !== 'all' && r.inflationOption !== filterInflation) {
        return false;
      }
      if (filterVerdict !== 'all' && r.verdict !== filterVerdict) {
        return false;
      }
      return true;
    });
  }, [results, filterReturn, filterInflation, filterVerdict]);

  // Aggregation je Rendite-Modell.
  const modelAggregation = useMemo(() => {
    return INVESTMENT_RETURN_OPTIONS.map(option => {
      const cells = results.filter(r => r.investmentReturnOption === option);
      return {
        option,
        total: cells.length,
        wins3: cells.filter(r => r.verdict === '3topf').length,
        wins1: cells.filter(r => r.verdict === '1topf').length,
        mixed: cells.filter(r => r.verdict === 'gemischt').length,
        undecided: cells.filter(r => r.verdict === 'unentschieden').length,
      };
    }).filter(row => row.total > 0);
  }, [results]);

  const selectOption = (value: string, label: string) => ({ value, label });

  return (
    <SpaceBetween direction="vertical" size="l">
      {/* 1. Erklär-Alert */}
      <Alert type="info" header="Wann gewinnt der 3-Topf im Tail-Risiko?">
        <SpaceBetween direction="vertical" size="xs">
          <Box>
            Getestet wird die <strong>volle Szenario-Matrix</strong>:{' '}
            {INFLATION_OPTIONS.length} Inflations- × {INVESTMENT_RETURN_OPTIONS.length}{' '}
            Rendite-Optionen über einen Entnahmeraten-Sweep. Je Zelle laufen beide
            Strategien über identische Markt-/Ratenpfade.
          </Box>
          <Box>
            Das Urteil basiert bewusst auf <strong>Tail-Metriken</strong>, nicht auf
            Mittelwerten: P10-Endkapital (schlechte 10 % der Ausgänge, verarmte Läufe
            zählen als 0 €) und Verarmungsrisiko. So wird die eigentliche Wertfrage
            beantwortet — schützt der 3-Topf im schlechten Ausgang?
          </Box>
          {simulationOptions && (
            <Box variant="small" color="text-body-secondary">
              Feste Annahmen (wie gewählt): Tagesgeld „
              {getInvestmentReturnShortLabel(simulationOptions.tagesgeldOption)}", Zinstopf „
              {getInvestmentReturnShortLabel(simulationOptions.zinstopfOption)}". Nur
              Inflation und Investitionsrendite werden über die Matrix variiert.
            </Box>
          )}
          <Box variant="small" color="text-body-secondary">
            Hinweis: Bei niedriger Iterationszahl je Zelle enthalten die Tail-Metriken
            statistisches Rauschen. Knappe Vorteile werden über Epsilon-Schwellen als
            „gleichauf" behandelt.
          </Box>
        </SpaceBetween>
      </Alert>

      {/* 2. Konfiguration */}
      <Container
        header={
          <Header variant="h2" description="Entnahmeraten-Bereich und Iterationen je Zelle">
            Szenario-Matrix Konfiguration
          </Header>
        }
      >
        <SpaceBetween direction="vertical" size="m">
          <ColumnLayout columns={2}>
            <SpaceBetween direction="vertical" size="s">
              <Box variant="h3">Entnahme-Rate (%)</Box>
              <FormField label="Minimale Rate (%)">
                <Input
                  type="number"
                  value={config.minRate.toString()}
                  onChange={({ detail }) => {
                    const value = parseFloat(detail.value) || 0;
                    setConfig({ ...config, minRate: Math.max(0.1, Math.min(value, 20)) });
                  }}
                  step={0.5}
                  disabled={isRunning}
                />
              </FormField>
              <FormField label="Maximale Rate (%)">
                <Input
                  type="number"
                  value={config.maxRate.toString()}
                  onChange={({ detail }) => {
                    const value = parseFloat(detail.value) || 0;
                    setConfig({ ...config, maxRate: Math.max(0.1, Math.min(value, 20)) });
                  }}
                  step={0.5}
                  disabled={isRunning}
                />
              </FormField>
              <FormField label="Schrittweite (%)">
                <Input
                  type="number"
                  value={config.stepSize.toString()}
                  onChange={({ detail }) => {
                    const value = parseFloat(detail.value) || 0;
                    setConfig({ ...config, stepSize: Math.max(0.25, Math.min(value, 5)) });
                  }}
                  step={0.25}
                  disabled={isRunning}
                />
              </FormField>
            </SpaceBetween>

            <SpaceBetween direction="vertical" size="s">
              <Box variant="h3">Iterationen je Zelle</Box>
              <FormField
                label="Monte-Carlo-Iterationen pro Zelle"
                description="100–2000. Mehr Iterationen = stabilere Tail-Metriken, aber längere Laufzeit."
              >
                <Input
                  type="number"
                  value={config.iterationsPerCell.toString()}
                  onChange={({ detail }) => {
                    const value = parseInt(detail.value, 10) || 100;
                    setConfig({
                      ...config,
                      iterationsPerCell: Math.max(100, Math.min(value, 2000)),
                    });
                  }}
                  step={100}
                  disabled={isRunning}
                />
              </FormField>
            </SpaceBetween>
          </ColumnLayout>

          <Box>
            <Box variant="awsui-key-label">Gesamtanzahl Zellen</Box>
            <Box variant="p">
              {cellCount} Zellen ({BASE_CELLS_PER_RATE} × {rateCount} Entnahmeraten) ×{' '}
              {config.iterationsPerCell} Iterationen = {totalSims.toLocaleString('de-DE')}{' '}
              Simulationen
            </Box>
            <Box variant="small" color="text-body-secondary">
              Geschätzte Dauer: ~{estimatedMinutes} Minute(n) (blockiert wie die anderen
              Optimierungs-Tabs den Main-Thread).
            </Box>
          </Box>

          {totalSims > 100000 && (
            <Alert type="warning">
              <strong>Warnung:</strong> Diese Konfiguration erzeugt{' '}
              {totalSims.toLocaleString('de-DE')} Simulationen und kann sehr lange dauern
              (~{estimatedMinutes} Minuten). Reduzieren Sie den Raten-Bereich oder die
              Iterationen je Zelle.
            </Alert>
          )}

          <Box>
            <Button
              variant="primary"
              onClick={handleRun}
              disabled={isRunning || !monteCarloEngine || !steuerParameter || !isConfigValid}
              loading={isRunning}
            >
              {isRunning ? 'Matrix läuft...' : `Matrix berechnen (${cellCount} Zellen)`}
            </Button>
            {!isConfigValid && (
              <Box color="text-status-error" fontSize="body-s" margin={{ top: 'xs' }}>
                Minimale Rate muss kleiner als maximale Rate sein und die Schrittweite &gt; 0.
              </Box>
            )}
          </Box>
        </SpaceBetween>
      </Container>

      {/* 3. Progress */}
      {isRunning && (
        <Container>
          <Box>
            <Box variant="h4">Szenario-Matrix läuft...</Box>
            {currentCellLabel !== undefined &&
              currentCellIndex !== undefined &&
              totalCells !== undefined && (
                <Box variant="small" color="text-body-secondary" margin={{ bottom: 'xs' }}>
                  Zelle {currentCellIndex} von {totalCells}: {currentCellLabel} | Fortschritt:{' '}
                  {progress}%
                </Box>
              )}
            <div
              style={{
                width: '100%',
                height: '8px',
                backgroundColor: '#e9ecef',
                borderRadius: '4px',
                overflow: 'hidden',
              }}
            >
              <div
                style={{
                  width: `${progress}%`,
                  height: '100%',
                  backgroundColor: '#0073bb',
                  transition: 'width 0.3s ease',
                }}
              />
            </div>
          </Box>
        </Container>
      )}

      {/* 4. KPI-Karten */}
      {results.length > 0 && !isRunning && (
        <Container header={<Header variant="h2">Tail-Urteil über alle {results.length} Zellen</Header>}>
          <SpaceBetween direction="vertical" size="m">
            <ColumnLayout columns={4} variant="text-grid">
              <Box>
                <Box variant="awsui-key-label">3-Topf gewinnt im Tail</Box>
                <Box variant="h2" color="text-status-success">
                  {kpi.wins3} <Box variant="span" color="text-body-secondary">von {results.length}</Box>
                </Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">1-Topf gewinnt im Tail</Box>
                <Box variant="h2" color="text-status-info">
                  {kpi.wins1} <Box variant="span" color="text-body-secondary">von {results.length}</Box>
                </Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Gemischt</Box>
                <Box variant="h2">{kpi.mixed}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Unentschieden</Box>
                <Box variant="h2">{kpi.undecided}</Box>
              </Box>
            </ColumnLayout>
            <ColumnLayout columns={4} variant="text-grid">
              <Box>
                <Box variant="awsui-key-label">P10-Siege 3-Topf</Box>
                <Box variant="p">{kpi.p10Three}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">P10-Siege 1-Topf</Box>
                <Box variant="p">{kpi.p10One}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Risiko-Siege 3-Topf</Box>
                <Box variant="p">{kpi.riskThree}</Box>
              </Box>
              <Box>
                <Box variant="awsui-key-label">Risiko-Siege 1-Topf</Box>
                <Box variant="p">{kpi.riskOne}</Box>
              </Box>
            </ColumnLayout>
          </SpaceBetween>
        </Container>
      )}

      {/* 5. Ehrliches Fazit */}
      {verdictSummary && !isRunning && (
        <Alert type={verdictSummary.type} header={verdictSummary.header}>
          {verdictSummary.body}
        </Alert>
      )}

      {/* 6. Matrix-Tabelle */}
      {results.length > 0 && !isRunning && (
        <Container
          header={
            <Header variant="h2" counter={`(${filteredResults.length}/${results.length})`}>
              Matrix je Zelle
            </Header>
          }
        >
          <SpaceBetween direction="vertical" size="m">
            <ColumnLayout columns={3}>
              <FormField label="Filter Rendite-Modell">
                <Select
                  selectedOption={
                    filterReturn === 'all'
                      ? selectOption('all', 'Alle Modelle')
                      : selectOption(filterReturn, getInvestmentReturnShortLabel(filterReturn))
                  }
                  onChange={({ detail }) => setFilterReturn(detail.selectedOption?.value || 'all')}
                  options={[
                    selectOption('all', 'Alle Modelle'),
                    ...INVESTMENT_RETURN_OPTIONS.map(o =>
                      selectOption(o, getInvestmentReturnShortLabel(o))
                    ),
                  ]}
                  expandToViewport
                />
              </FormField>
              <FormField label="Filter Inflation">
                <Select
                  selectedOption={
                    filterInflation === 'all'
                      ? selectOption('all', 'Alle Inflations-Modelle')
                      : selectOption(filterInflation, getInflationShortLabel(filterInflation))
                  }
                  onChange={({ detail }) =>
                    setFilterInflation(detail.selectedOption?.value || 'all')
                  }
                  options={[
                    selectOption('all', 'Alle Inflations-Modelle'),
                    ...INFLATION_OPTIONS.map(o => selectOption(o, getInflationShortLabel(o))),
                  ]}
                  expandToViewport
                />
              </FormField>
              <FormField label="Filter Urteil">
                <Select
                  selectedOption={selectOption(
                    filterVerdict,
                    filterVerdict === 'all'
                      ? 'Alle Urteile'
                      : filterVerdict === '3topf'
                        ? '3-Topf'
                        : filterVerdict === '1topf'
                          ? '1-Topf'
                          : filterVerdict === 'gemischt'
                            ? 'Gemischt'
                            : 'Unentschieden'
                  )}
                  onChange={({ detail }) => setFilterVerdict(detail.selectedOption?.value || 'all')}
                  options={[
                    selectOption('all', 'Alle Urteile'),
                    selectOption('3topf', '3-Topf'),
                    selectOption('1topf', '1-Topf'),
                    selectOption('gemischt', 'Gemischt'),
                    selectOption('unentschieden', 'Unentschieden'),
                  ]}
                  expandToViewport
                />
              </FormField>
            </ColumnLayout>

            <Table
              variant="embedded"
              columnDefinitions={[
                {
                  id: 'model',
                  header: 'Rendite-Modell',
                  cell: item => getInvestmentReturnShortLabel(item.investmentReturnOption),
                },
                {
                  id: 'inflation',
                  header: 'Inflation',
                  cell: item => getInflationShortLabel(item.inflationOption),
                },
                {
                  id: 'rate',
                  header: 'Rate',
                  cell: item => `${item.withdrawalRate}%`,
                },
                {
                  id: 'p10Three',
                  header: 'P10 3-Topf',
                  cell: item => formatEur(item.threeTopf.p10),
                },
                {
                  id: 'p10One',
                  header: 'P10 1-Topf',
                  cell: item => formatEur(item.oneTopf.p10),
                },
                {
                  id: 'p10Delta',
                  header: 'ΔP10',
                  cell: item => {
                    const d = relativeP10Delta(item);
                    const sign = d >= 0 ? '+' : '−';
                    return `${sign}${Math.abs(d).toFixed(0)} %`;
                  },
                },
                {
                  id: 'riskThree',
                  header: 'Risiko 3-Topf',
                  cell: item => `${item.threeTopf.depletionRisk.toFixed(1)}%`,
                },
                {
                  id: 'riskOne',
                  header: 'Risiko 1-Topf',
                  cell: item => `${item.oneTopf.depletionRisk.toFixed(1)}%`,
                },
                {
                  id: 'riskDelta',
                  header: 'ΔRisiko',
                  cell: item => {
                    const d = item.threeTopf.depletionRisk - item.oneTopf.depletionRisk;
                    const sign = d >= 0 ? '+' : '−';
                    return `${sign}${Math.abs(d).toFixed(1).replace('.', ',')} pp`;
                  },
                },
                {
                  id: 'medianThree',
                  header: 'Median 3-Topf',
                  cell: item => formatEur(item.threeTopf.median),
                },
                {
                  id: 'medianOne',
                  header: 'Median 1-Topf',
                  cell: item => formatEur(item.oneTopf.median),
                },
                {
                  id: 'verdict',
                  header: 'Urteil',
                  cell: item => (
                    <SpaceBetween direction="vertical" size="xxs">
                      {verdictBadge(item.verdict)}
                      <Box variant="small" color="text-body-secondary">
                        {item.verdictReason}
                      </Box>
                    </SpaceBetween>
                  ),
                },
              ]}
              items={filteredResults}
              sortingDisabled={true}
              empty={
                <Box textAlign="center" color="text-body-secondary">
                  <p>Keine Zellen für diese Filter</p>
                </Box>
              }
            />
          </SpaceBetween>
        </Container>
      )}

      {/* 7. Aggregation je Rendite-Modell */}
      {results.length > 0 && !isRunning && (
        <Container header={<Header variant="h2">Aggregation je Rendite-Modell</Header>}>
          <Table
            variant="embedded"
            columnDefinitions={[
              {
                id: 'model',
                header: 'Rendite-Modell',
                cell: item => getInvestmentReturnShortLabel(item.option),
              },
              { id: 'total', header: 'Zellen', cell: item => item.total.toString() },
              { id: 'wins3', header: '3-Topf-Siege', cell: item => item.wins3.toString() },
              { id: 'wins1', header: '1-Topf-Siege', cell: item => item.wins1.toString() },
              { id: 'mixed', header: 'Gemischt', cell: item => item.mixed.toString() },
              {
                id: 'undecided',
                header: 'Unentschieden',
                cell: item => item.undecided.toString(),
              },
            ]}
            items={modelAggregation}
            sortingDisabled={true}
            empty={
              <Box textAlign="center" color="text-body-secondary">
                <p>Keine Daten verfügbar</p>
              </Box>
            }
          />
        </Container>
      )}
    </SpaceBetween>
  );
};
